import { ref, onMounted } from 'vue';

/**
 * Composable for managing scenario selection from Workflow sheet
 * Reads all CreateScenario actions to populate scenario list
 */
export function useScenarioSelection() {
    const scenarios = ref([]);

    /**
     * Reads the Workflow sheet and extracts scenario names from CreateScenario actions
     * Based on VBA getScenarios() function from ProcessWorkflow.bas
     */
    const fetchScenarios = async () => {
        try {
            await window.Excel.run(async (context) => {
                const workflowSheet = context.workbook.worksheets.getItemOrNullObject('Workflow');
                await context.sync();

                if (workflowSheet.isNullObject) {
                    // Workflow sheet doesn't exist yet
                    scenarios.value = [];
                    return;
                }

                // Get the used range to find all actions
                const usedRange = workflowSheet.getUsedRangeOrNullObject();
                usedRange.load('values, columnCount');
                await context.sync();

                if (usedRange.isNullObject) {
                    // Empty workflow sheet
                    scenarios.value = [];
                    return;
                }

                const values = usedRange.values;
                const scenarioList = [];

                // Loop through each column (each column is a workflow action)
                for (let col = 0; col < usedRange.columnCount; col += 2) {
                    // Each action uses 2 columns: keys and values
                    const keysCol = col;
                    const valuesCol = col + 1;

                    if (valuesCol >= values[0].length) break;

                    // Check if this is an action column (row 1 should have ActionName)
                    const firstKey = values[0][keysCol];
                    if (firstKey !== 'ActionName') continue;

                    // Check if this is a CreateScenario action (row 2 should have ActionType)
                    const actionTypeKey = values[1]?.[keysCol];
                    const actionTypeValue = values[1]?.[valuesCol];

                    if (actionTypeKey === 'ActionType' && actionTypeValue === 'CreateScenario') {
                        // Loop down to find ScenarioName parameter
                        for (let row = 0; row < values.length; row++) {
                            const paramName = values[row][keysCol];
                            const paramValue = values[row][valuesCol];

                            if (paramName === 'ScenarioName' && paramValue) {
                                scenarioList.push(paramValue);
                                break;
                            }
                        }
                    }
                }

                scenarios.value = scenarioList;
            });
        } catch (error) {
            console.error('Error fetching scenarios from Workflow sheet:', error);
            scenarios.value = [];
        }
    };

    // Initialize on mount
    onMounted(fetchScenarios);

    return {
        scenarios,
        fetchScenarios
    };
}
